/* eslint-disable no-var, react/no-children-prop, sonarjs/no-nested-functions */
import React, { useState, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

/**
 * Using inline CSS so that no dependencies are coming
 *
 * ( want it to work no matter whether we use CSS Modules, or any CSS in JS )
 */

var Backdrop = function Backdrop(_a) {
  var children = _a.children;
  return React.createElement("div", {
    "data-dialog-root": true,
    style: {
      position: "fixed",
      backgroundColor: "rgba(24, 24, 36, 0.7)",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }
  }, children);
};

var Container = function Container(_a) {
  var children = _a.children;
  return React.createElement("div", {
    style: {
      width: 600,
      maxWidth: "100%",
      background: "#fff",
      color: "#090909",
      borderRadius: 8
    }
  }, children);
};

var Body = function Body(_a) {
  var children = _a.children;
  return React.createElement("div", {
    style: {
      padding: 16
    }
  }, children);
};

var Footer = function Footer(_a) {
  var children = _a.children;
  return React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "flex-end",
      padding: "8px 8px 8px 12px",
      borderTop: "1px solid #ddd"
    }
  }, children);
};

var Button = function Button(_a) {
  var type = _a.type,
      onClick = _a.onClick,
      children = _a.children;
  return React.createElement("button", {
    onClick: onClick,
    style: {
      appearance: "none",
      cursor: "pointer",
      border: 0,
      borderRadius: 4,
      margin: "0 4px",
      fontSize: "0.875em",
      backgroundColor: type === "primary" ? "#4466ee" : "#646464",
      color: "#fff",
      display: "flex",
      justifyContent: "flex-end",
      padding: "8px 16px",
      borderTop: "1px solid #ddd"
    }
  }, children);
};

var DefaultLayout = function DefaultLayout(_a) {
  var labels = _a.labels,
      children = _a.children,
      onOk = _a.onOk,
      onCancel = _a.onCancel;
  return React.createElement(Backdrop, null, React.createElement(Container, null, React.createElement(Body, null, children), React.createElement(Footer, null, React.createElement(Button, {
    type: "primary",
    onClick: onOk
  }, labels.ok), onCancel && React.createElement(Button, {
    type: "default",
    onClick: onCancel
  }, labels.cancel))));
};

var Dialog = createContext({
  alert: function alert(_children, _labels) {
    throw new Error("[react-async-dialog] Please render <DialogProvider> above in your tree, to use alert()");
  },
  confirm: function confirm(_children, _labels) {
    throw new Error("[react-async-dialog] Please render <DialogProvider> above in your tree, to use confirm()");
  },
  portal: function portal(_children, _labels) {
    throw new Error("[react-async-dialog] Please render <DialogProvider> above in your tree, to use portal()");
  }
});
var defaultLabels = {
  ok: "OK",
  cancel: "Cancel"
};
function DialogProvider(_a) {
  var _b = _a.layout,
      layout = _b === void 0 ? DefaultLayout : _b,
      _c = _a.container,
      container = _c === void 0 ? document.body : _c,
      children = _a.children;

  var _d = useState(null),
      portal = _d[0],
      setPortal = _d[1];

  var createOpener = useCallback(function (Component, okOnly) {
    return function (children, labels) {
      return new Promise(function (resolve) {
        var _a, _b, _c, _d;

        var onResolve = function onResolve(answer) {
          return function (e) {
            e.preventDefault();
            setPortal(null);
            resolve(answer);
          };
        };

        var portal = createPortal(React.createElement(Component, {
          onOk: onResolve(true),
          onCancel: okOnly ? undefined : onResolve(false),
          children: children,
          labels: {
            ok: (_b = (_a = labels) === null || _a === void 0 ? void 0 : _a.ok, _b !== null && _b !== void 0 ? _b : defaultLabels.ok),
            cancel: (_d = (_c = labels) === null || _c === void 0 ? void 0 : _c.cancel, _d !== null && _d !== void 0 ? _d : defaultLabels.cancel)
          }
        }), container);
        setPortal(portal);
      });
    };
  }, [children, container]);
  return React.createElement(Dialog.Provider, {
    value: {
      alert: createOpener(layout, true),
      confirm: createOpener(layout),
      portal: createOpener(React.Fragment)
    }
  }, children, portal);
}

function useDialog() {
  return useContext(Dialog);
}

export default DialogProvider;
export { DefaultLayout, useDialog };
